/*
 * SPDX-FileCopyrightText: 2026 Devis Meneghelli
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */

/**
 * Registrazione del service worker e delle notifiche push (approvazione
 * dispositivi mail e altre notifiche di sicurezza).
 *
 * Due funzioni con tempistiche diverse, entrambe pensate per fallire in
 * silenzio (loggando al massimo in console) — un browser senza
 * supporto, un permesso negato, o un errore di rete non devono MAI
 * bloccare o rallentare l'avvio del resto dell'app:
 *
 * - registerServiceWorker(): va chiamata SUBITO all'avvio (vedi
 *   index.tsx), indipendentemente dal login. Necessaria perché l'app
 *   sia installabile come PWA già dalla pagina di login.
 * - registerPushNotifications(): resta successiva al login, dato che
 *   la sottoscrizione push richiede comunque una sessione autenticata.
 */

function base64UrlToUint8Array(base64Url: string): Uint8Array {
	const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
	const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; i += 1) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}

function isServiceWorkerSupported(): boolean {
	return 'serviceWorker' in navigator;
}

function isPushSupported(): boolean {
	return isServiceWorkerSupported() && 'PushManager' in window && 'Notification' in window;
}

async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
	await fetch('/service/push/subscribe', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ subscription: subscription.toJSON() })
	});
}

/**
 * Registra il service worker, indipendentemente da notifiche push e da
 * login. Va chiamata IL PRIMA POSSIBILE all'avvio dell'app (vedi
 * index.tsx), non dopo il login: i browser valutano i criteri di
 * installabilità PWA (manifest + service worker attivo) già dalla
 * pagina di login stessa — se la registrazione avvenisse solo dopo
 * l'autenticazione, l'app non risulterebbe installabile finché
 * l'utente non avesse già effettuato l'accesso almeno una volta.
 * Fallisce in silenzio come il resto di questo file: un browser senza
 * supporto o un errore di rete non devono mai bloccare l'avvio.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
	if (!isServiceWorkerSupported()) return undefined;
	try {
		return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
	} catch (error) {
		console.warn('Registrazione service worker non riuscita:', error);
		return undefined;
	}
}

/**
 * Chiede il permesso per le notifiche (solo se l'utente non ha già
 * scelto in passato) e sottoscrive alle push, riusando la
 * registrazione del service worker già effettuata da
 * registerServiceWorker() all'avvio (o registrandolo se per qualche
 * motivo non fosse ancora avvenuto). Va chiamata DOPO un login
 * riuscito: non ha senso proporla prima che l'utente sia autenticato,
 * e l'endpoint di sottoscrizione richiede comunque una sessione valida.
 */
export async function registerPushNotifications(): Promise<void> {
	if (!isPushSupported()) return;

	// Non richiediamo il permesso se l'utente lo ha già negato in
	// passato: ripresentare la richiesta ad ogni caricamento pagina
	// sarebbe fastidioso e nella maggior parte dei browser moderni non
	// funzionerebbe comunque (richiesta silenziosamente ignorata).
	if (Notification.permission === 'denied') return;

	try {
		const registration =
			(await navigator.serviceWorker.getRegistration('/')) ?? (await registerServiceWorker());
		if (!registration) return;

		const existingSubscription = await registration.pushManager.getSubscription();
		if (existingSubscription) {
			// Già sottoscritto in una sessione precedente: ci assicuriamo
			// solo che il backend abbia ancora questa sottoscrizione
			// registrata (es. dopo un riavvio del gateway con database
			// pulito), rimandandola senza disturbare l'utente.
			await sendSubscriptionToServer(existingSubscription);
			return;
		}

		if (Notification.permission !== 'granted') {
			const permission = await Notification.requestPermission();
			if (permission !== 'granted') return;
		}

		const vapidResponse = await fetch('/service/push/vapid-public-key');
		if (!vapidResponse.ok) return;
		const { publicKey } = await vapidResponse.json();
		if (!publicKey) return;

		const subscription = await registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: base64UrlToUint8Array(publicKey) as BufferSource
		});

		await sendSubscriptionToServer(subscription);
	} catch (error) {
		// Fallimento silenzioso deliberato — vedi commento in cima al file.
		console.warn('Registrazione notifiche push non riuscita:', error);
	}
}
